import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Search,
  Plus,
  MoreVertical,
  Phone,
  Mail,
  FileText,
  Calendar,
  Edit,
  Trash2,
  X,
  Building,
  MapPin,
  Loader2,
  Filter,
  Users,
  Tag,
  CheckSquare,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
} from "lucide-react";

const ITEMS_PER_PAGE = 100;

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [showBulkEventModal, setShowBulkEventModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Sorting state
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  
  // Bulk operation progress state
  const [bulkProgress, setBulkProgress] = useState({ show: false, current: 0, total: 0, action: "" });

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    homepage: "",
    linkedin: "",
    address: "",
    notes: "",
    tag_ids: [],
  });

  const [eventData, setEventData] = useState({
    type: "call",
    description: "",
    date: new Date().toISOString().split("T")[0],
  });

  const [bulkTagIds, setBulkTagIds] = useState([]);
  const [bulkEventData, setBulkEventData] = useState({
    type: "call",
    description: "",
    date: new Date().toISOString().split("T")[0],
  });

  // Get "Neu" tag ID
  const neuTagId = useMemo(() => {
    const neuTag = tags.find(t => t.name === "Neu");
    return neuTag?.id;
  }, [tags]);

  // Filter for Kunden only (no "Neu" tag OR has events)
  const kundenItems = useMemo(() => {
    if (!neuTagId) return customers;
    return customers.filter(customer => {
      const hasNeuTag = customer.tag_ids?.includes(neuTagId);
      const hasEvents = customer.events?.length > 0;
      return !hasNeuTag || hasEvents;
    });
  }, [customers, neuTagId]);

  // Fetch data
  const fetchData = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterTag && filterTag !== "all") params.tag_id = filterTag;

      const [customersRes, tagsRes] = await Promise.all([
        apiClient.get("/customers", { params }),
        apiClient.get("/tags"),
      ]);
      setCustomers(customersRes.data);
      setTags(tagsRes.data);
      setSelectedIds(new Set());
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [search, filterTag]);

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [search, filterTag]);

  // Sorted customers - empty values always at the bottom
  const sortedCustomers = useMemo(() => {
    return [...kundenItems].sort((a, b) => {
      const aRaw = a[sortField];
      const bRaw = b[sortField];
      
      if (sortField === "created_at") {
        const aVal = new Date(aRaw || 0).getTime();
        const bVal = new Date(bRaw || 0).getTime();
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      
      const aVal = (aRaw || "").toString().toLowerCase().trim();
      const bVal = (bRaw || "").toString().toLowerCase().trim();
      
      const aEmpty = !aVal;
      const bEmpty = !bVal;
      
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (aEmpty && bEmpty) return 0;
      
      return sortDirection === "asc" 
        ? aVal.localeCompare(bVal, 'de')
        : bVal.localeCompare(aVal, 'de');
    });
  }, [kundenItems, sortField, sortDirection]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedCustomers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedCustomers = sortedCustomers.slice(startIndex, endIndex);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setCurrentPage(1);
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-40" />;
    }
    return sortDirection === "asc" 
      ? <ArrowUp className="w-4 h-4 ml-1 text-indigo-600" />
      : <ArrowDown className="w-4 h-4 ml-1 text-indigo-600" />;
  };

  // Selection handlers
  const toggleSelectAll = () => {
    const currentPageIds = paginatedCustomers.map(c => c.id);
    const allSelected = currentPageIds.every(id => selectedIds.has(id));
    
    if (allSelected) {
      const newSelected = new Set(selectedIds);
      currentPageIds.forEach(id => newSelected.delete(id));
      setSelectedIds(newSelected);
    } else {
      const newSelected = new Set(selectedIds);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedIds(newSelected);
    }
  };

  const toggleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const currentPageIds = paginatedCustomers.map(c => c.id);
  const isAllSelected = paginatedCustomers.length > 0 && currentPageIds.every(id => selectedIds.has(id));

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      company: "",
      homepage: "",
      linkedin: "",
      address: "",
      notes: "",
      tag_ids: [],
    });
  };

  // Handle add/edit customer
  const handleSaveCustomer = async () => {
    if (!formData.name.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }

    setSaving(true);
    try {
      if (selectedCustomer) {
        await apiClient.put(`/customers/${selectedCustomer.id}`, formData);
        toast.success("Kunde aktualisiert");
      } else {
        await apiClient.post("/customers", formData);
        toast.success("Kunde erstellt");
      }
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  // Handle delete customer
  const handleDeleteCustomer = async (id) => {
    if (!window.confirm("Diesen Kunden in den Papierkorb verschieben?")) return;
    
    try {
      await apiClient.delete(`/customers/${id}`);
      toast.success("In den Papierkorb verschoben");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  // Handle add event
  const handleAddEvent = async () => {
    if (!eventData.description.trim()) {
      toast.error("Beschreibung ist erforderlich");
      return;
    }

    setSaving(true);
    try {
      await apiClient.post(`/customers/${selectedCustomer.id}/events`, eventData);
      toast.success("Ereignis hinzugefügt");
      setShowEventModal(false);
      setEventData({ type: "call", description: "", date: new Date().toISOString().split("T")[0] });
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Hinzufügen");
    } finally {
      setSaving(false);
    }
  };

  // Bulk Delete
  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    if (!window.confirm(`Sind Sie sicher, dass Sie ${count} Kunden löschen möchten?`)) {
      return;
    }

    const ids = Array.from(selectedIds);
    setBulkProgress({ show: true, current: 0, total: ids.length, action: "Lösche Kunden" });
    
    try {
      let successCount = 0;
      for (let i = 0; i < ids.length; i++) {
        await apiClient.delete(`/customers/${ids[i]}`);
        successCount++;
        setBulkProgress(prev => ({ ...prev, current: i + 1 }));
      }
      toast.success(`${successCount} Kunden gelöscht`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    } finally {
      setBulkProgress({ show: false, current: 0, total: 0, action: "" });
    }
  };

  // Bulk Add Tags
  const handleBulkAddTags = async () => {
    if (bulkTagIds.length === 0) {
      toast.error("Bitte wählen Sie mindestens einen Tag aus");
      return;
    }

    const ids = Array.from(selectedIds);
    setShowBulkTagModal(false);
    setBulkProgress({ show: true, current: 0, total: ids.length, action: "Füge Tags hinzu" });
    
    try {
      let successCount = 0;
      for (let i = 0; i < ids.length; i++) {
        const customer = customers.find(c => c.id === ids[i]);
        if (customer) {
          const newTagIds = [...new Set([...customer.tag_ids, ...bulkTagIds])];
          await apiClient.put(`/customers/${ids[i]}`, { tag_ids: newTagIds });
          successCount++;
        }
        setBulkProgress(prev => ({ ...prev, current: i + 1 }));
      }
      toast.success(`Tags zu ${successCount} Kunden hinzugefügt`);
      setBulkTagIds([]);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Hinzufügen der Tags");
    } finally {
      setBulkProgress({ show: false, current: 0, total: 0, action: "" });
    }
  };

  // Bulk Add Event
  const handleBulkAddEvent = async () => {
    if (!bulkEventData.description.trim()) {
      toast.error("Beschreibung ist erforderlich");
      return;
    }

    const ids = Array.from(selectedIds);
    setShowBulkEventModal(false);
    setBulkProgress({ show: true, current: 0, total: ids.length, action: "Füge Ereignisse hinzu" });
    
    try {
      let successCount = 0;
      for (let i = 0; i < ids.length; i++) {
        await apiClient.post(`/customers/${ids[i]}/events`, bulkEventData);
        successCount++;
        setBulkProgress(prev => ({ ...prev, current: i + 1 }));
      }
      toast.success(`Ereignis zu ${successCount} Kunden hinzugefügt`);
      setBulkEventData({ type: "call", description: "", date: new Date().toISOString().split("T")[0] });
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Hinzufügen des Ereignisses");
    } finally {
      setBulkProgress({ show: false, current: 0, total: 0, action: "" });
    }
  };

  // Open edit modal
  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      company: customer.company || "",
      homepage: customer.homepage || "",
      linkedin: customer.linkedin || "",
      address: customer.address || "",
      notes: customer.notes || "",
      tag_ids: customer.tag_ids || [],
    });
    setShowAddModal(true);
  };

  // Open detail full page
  const openDetailModal = (customer) => {
    navigate(`/customers/${customer.id}`);
  };

  // Quick add event from table
  const quickAddEvent = (customer, type) => {
    setSelectedCustomer(customer);
    setEventData({
      type,
      description: "",
      date: new Date().toISOString().split("T")[0],
    });
    setShowEventModal(true);
  };

  // Get tag color style
  const getTagStyle = (color) => ({
    backgroundColor: `${color}20`,
    color: color,
    borderColor: `${color}40`,
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Event type icons
  const eventIcons = {
    call: { icon: Phone, class: "event-icon-call" },
    email: { icon: Mail, class: "event-icon-email" },
    note: { icon: FileText, class: "event-icon-note" },
    meeting: { icon: Calendar, class: "event-icon-meeting" },
    other: { icon: FileText, class: "event-icon-other" },
  };

  const eventTypeLabels = {
    call: "Anruf",
    email: "E-Mail",
    note: "Notiz",
    meeting: "Meeting",
    other: "Sonstiges",
  };

  return (
    <Layout title="Kunden">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6" data-testid="customers-header">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Suchen nach Name, Firma..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-10 h-11 border-slate-200"
            data-testid="search-input"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="clear-search-btn"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>

        <Select value={filterTag} onValueChange={setFilterTag}>
          <SelectTrigger className="w-48 h-11" data-testid="filter-tag-select">
            <Filter className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Nach Tag filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Tags</SelectItem>
            {tags.map((tag) => (
              <SelectItem key={tag.id} value={tag.id}>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                  <span className="truncate">{tag.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={() => {
            setSelectedCustomer(null);
            resetForm();
            setShowAddModal(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 h-11 shadow-sm"
          data-testid="add-customer-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Kunde
        </Button>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4 flex items-center justify-between animate-fadeIn" data-testid="bulk-action-bar">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5 text-indigo-600" />
            <span className="font-medium text-indigo-900">
              {selectedIds.size} {selectedIds.size === 1 ? "Kunde" : "Kunden"} ausgewählt
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setBulkTagIds([]);
                setShowBulkTagModal(true);
              }}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
              data-testid="bulk-tag-btn"
            >
              <Tag className="w-4 h-4 mr-2" />
              Tags hinzufügen
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setBulkEventData({ type: "call", description: "", date: new Date().toISOString().split("T")[0] });
                setShowBulkEventModal(true);
              }}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
              data-testid="bulk-event-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Ereignis hinzufügen
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkDelete}
              className="border-red-300 text-red-700 hover:bg-red-100"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Löschen
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              className="text-slate-600"
            >
              <X className="w-4 h-4 mr-1" />
              Auswahl aufheben
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="customers-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={toggleSelectAll}
                    className="border-slate-300"
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead 
                  className="font-semibold text-slate-600 cursor-pointer hover:text-indigo-600 transition-colors select-none"
                  onClick={() => handleSort("name")}
                  data-testid="sort-name"
                >
                  <div className="flex items-center">
                    Name
                    <SortIcon field="name" />
                  </div>
                </TableHead>
                <TableHead 
                  className="font-semibold text-slate-600 cursor-pointer hover:text-indigo-600 transition-colors select-none"
                  onClick={() => handleSort("company")}
                  data-testid="sort-company"
                >
                  <div className="flex items-center">
                    Firma
                    <SortIcon field="company" />
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-slate-600">Tags</TableHead>
                <TableHead className="font-semibold text-slate-600">Quick-Actions</TableHead>
                <TableHead 
                  className="font-semibold text-slate-600 cursor-pointer hover:text-indigo-600 transition-colors select-none"
                  onClick={() => handleSort("created_at")}
                  data-testid="sort-created"
                >
                  <div className="flex items-center">
                    Erstellt
                    <SortIcon field="created_at" />
                  </div>
                </TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : paginatedCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">Keine Kunden gefunden</p>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCustomers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    className={`customer-row cursor-pointer ${selectedIds.has(customer.id) ? "bg-indigo-50/50" : ""}`}
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(customer.id)}
                        onCheckedChange={() => toggleSelectOne(customer.id)}
                        className="border-slate-300"
                      />
                    </TableCell>
                    <TableCell 
                      className="font-medium text-slate-900"
                      onClick={() => openDetailModal(customer)}
                    >
                      {customer.name}
                    </TableCell>
                    <TableCell 
                      className="text-slate-600"
                      onClick={() => openDetailModal(customer)}
                    >
                      {customer.company || "-"}
                    </TableCell>
                    <TableCell onClick={() => openDetailModal(customer)}>
                      <div className="flex flex-wrap gap-1">
                        {customer.tags?.map((tag) => (
                          <Badge
                            key={tag.id}
                            variant="outline"
                            className="text-xs"
                            style={getTagStyle(tag.color)}
                          >
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <button
                          className="quick-action-btn"
                          onClick={() => quickAddEvent(customer, "call")}
                          title="Anruf hinzufügen"
                        >
                          <Phone className="w-4 h-4" />
                        </button>
                        <button
                          className="quick-action-btn"
                          onClick={() => quickAddEvent(customer, "email")}
                          title="E-Mail hinzufügen"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          className="quick-action-btn"
                          onClick={() => quickAddEvent(customer, "note")}
                          title="Notiz hinzufügen"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell 
                      className="text-slate-500 text-sm whitespace-nowrap"
                      onClick={() => openDetailModal(customer)}
                    >
                      {formatDateTime(customer.created_at)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="quick-action-btn">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditModal(customer)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Bearbeiten
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDeleteCustomer(customer.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Löschen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {!loading && sortedCustomers.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
            <div className="text-sm text-slate-600">
              Zeige {startIndex + 1} - {Math.min(endIndex, sortedCustomers.length)} von {sortedCustomers.length}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="h-8 w-8 p-0">
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="h-8 w-8 p-0">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1 px-2">
                <span className="text-sm text-slate-600">Seite</span>
                <span className="font-medium text-slate-900">{currentPage}</span>
                <span className="text-sm text-slate-600">von {totalPages || 1}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="h-8 w-8 p-0">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} className="h-8 w-8 p-0">
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Customer Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customer-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedCustomer ? "Kunde bearbeiten" : "Neuer Kunde"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Max Mustermann" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="max@beispiel.de" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon</Label>
                <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+49 123 456789" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Firma</Label>
              <Input id="company" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder="Musterfirma GmbH" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="homepage">Homepage</Label>
                <Input id="homepage" value={formData.homepage} onChange={(e) => setFormData({ ...formData, homepage: e.target.value })} placeholder="www.beispiel.de" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="linkedin">LinkedIn</Label>
                <Input id="linkedin" value={formData.linkedin} onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })} placeholder="Name oder Link" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Adresse</Label>
              <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Musterstraße 1, 12345 Musterstadt" />
            </div>
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50 min-h-[60px]">
                {tags.map((tag) => {
                  const isSelected = formData.tag_ids.includes(tag.id);
                  return (
                    <Badge
                      key={tag.id}
                      variant="outline"
                      className={`cursor-pointer transition-all flex items-center gap-1.5 pr-2.5 ${isSelected ? "ring-2 ring-offset-1 ring-indigo-500" : "opacity-60 hover:opacity-80"}`}
                      style={getTagStyle(tag.color)}
                      onClick={() => {
                        const newTags = isSelected ? formData.tag_ids.filter((id) => id !== tag.id) : [...formData.tag_ids, tag.id];
                        setFormData({ ...formData, tag_ids: newTags });
                      }}
                    >
                      {isSelected && <Check className="w-3.5 h-3.5" style={{ color: tag.color }} />}
                      {tag.name}
                    </Badge>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notizen</Label>
              <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Zusätzliche Informationen..." rows={3} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Abbrechen</Button>
            <Button onClick={handleSaveCustomer} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : selectedCustomer ? "Aktualisieren" : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ereignis hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Typ</Label>
              <Select value={eventData.type} onValueChange={(value) => setEventData({ ...eventData, type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call"><div className="flex items-center gap-2"><Phone className="w-4 h-4" /> Anruf</div></SelectItem>
                  <SelectItem value="email"><div className="flex items-center gap-2"><Mail className="w-4 h-4" /> E-Mail</div></SelectItem>
                  <SelectItem value="note"><div className="flex items-center gap-2"><FileText className="w-4 h-4" /> Notiz</div></SelectItem>
                  <SelectItem value="meeting"><div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Meeting</div></SelectItem>
                  <SelectItem value="other"><div className="flex items-center gap-2"><FileText className="w-4 h-4" /> Sonstiges</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Datum</Label>
              <Input type="date" value={eventData.date} onChange={(e) => setEventData({ ...eventData, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Beschreibung *</Label>
              <Textarea value={eventData.description} onChange={(e) => setEventData({ ...eventData, description: e.target.value })} placeholder="Was wurde besprochen?" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventModal(false)}>Abbrechen</Button>
            <Button onClick={handleAddEvent} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Tag Modal */}
      <Dialog open={showBulkTagModal} onOpenChange={setShowBulkTagModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-indigo-600" />Tags hinzufügen</DialogTitle>
            <DialogDescription>Tags zu {selectedIds.size} ausgewählten Kunden hinzufügen</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="mb-3 block">Tags auswählen</Label>
            <div className="flex flex-wrap gap-2 p-4 border rounded-lg bg-slate-50 min-h-[80px]">
              {tags.map((tag) => {
                const isSelected = bulkTagIds.includes(tag.id);
                return (
                  <Badge
                    key={tag.id}
                    variant="outline"
                    className={`cursor-pointer transition-all flex items-center gap-1.5 pr-2.5 ${isSelected ? "ring-2 ring-offset-1 ring-indigo-500" : "opacity-60 hover:opacity-80"}`}
                    style={getTagStyle(tag.color)}
                    onClick={() => setBulkTagIds(isSelected ? bulkTagIds.filter((id) => id !== tag.id) : [...bulkTagIds, tag.id])}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5" style={{ color: tag.color }} />}
                    {tag.name}
                  </Badge>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkTagModal(false)}>Abbrechen</Button>
            <Button onClick={handleBulkAddTags} disabled={saving || bulkTagIds.length === 0} className="bg-indigo-600 hover:bg-indigo-700">
              Zu {selectedIds.size} Kunden hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Event Modal */}
      <Dialog open={showBulkEventModal} onOpenChange={setShowBulkEventModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-indigo-600" />Ereignis hinzufügen</DialogTitle>
            <DialogDescription>Ereignis zu {selectedIds.size} ausgewählten Kunden hinzufügen</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Typ</Label>
              <Select value={bulkEventData.type} onValueChange={(value) => setBulkEventData({ ...bulkEventData, type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call"><div className="flex items-center gap-2"><Phone className="w-4 h-4" /> Anruf</div></SelectItem>
                  <SelectItem value="email"><div className="flex items-center gap-2"><Mail className="w-4 h-4" /> E-Mail</div></SelectItem>
                  <SelectItem value="note"><div className="flex items-center gap-2"><FileText className="w-4 h-4" /> Notiz</div></SelectItem>
                  <SelectItem value="meeting"><div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Meeting</div></SelectItem>
                  <SelectItem value="other"><div className="flex items-center gap-2"><FileText className="w-4 h-4" /> Sonstiges</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Datum</Label>
              <Input type="date" value={bulkEventData.date} onChange={(e) => setBulkEventData({ ...bulkEventData, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Beschreibung *</Label>
              <Textarea value={bulkEventData.description} onChange={(e) => setBulkEventData({ ...bulkEventData, description: e.target.value })} placeholder="Was wurde besprochen?" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkEventModal(false)}>Abbrechen</Button>
            <Button onClick={handleBulkAddEvent} disabled={saving || !bulkEventData.description.trim()} className="bg-indigo-600 hover:bg-indigo-700">
              Zu {selectedIds.size} Kunden hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Progress Overlay */}
      {bulkProgress.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="flex flex-col items-center">
              <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{bulkProgress.action}</h3>
              <p className="text-slate-600 mb-4">{bulkProgress.current} von {bulkProgress.total} verarbeitet</p>
              <div className="w-full bg-slate-200 rounded-full h-3 mb-2">
                <div className="bg-indigo-600 h-3 rounded-full transition-all duration-300" style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }} />
              </div>
              <p className="text-sm text-slate-500">{Math.round((bulkProgress.current / bulkProgress.total) * 100)}% abgeschlossen</p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
